import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getDestinations, showDistrict } from '../../api/destinationApi';
import { loadFestivalsApiFirst, loadGuidesByCategory } from '../../utils/cmsContentLoader';
import { Seo, Loading } from '../shared';
import ExploreHero from '../explore/ExploreHero';
import DiscoverySection from '../explore/DiscoverySection';
import QuickFactsBar from '../explore/QuickFactsBar';
import MonthGuide from '../explore/MonthGuide';
import ClimateChart from '../explore/ClimateChart';
import FaqAccordion from '../explore/FaqAccordion';
import DestinationCard from '../destinations/DestinationCard';
import { EXPLORE_IMAGES } from '../../data/exploreImages';
import {
	SEASONS,
	REGIONAL_RECS,
	PACKING_GUIDE,
	PHOTO_TIPS,
	BUDGET_SEASONS,
	SEASONAL_FAQS,
	SUGGESTED_ITINERARIES,
	CLIMATE_LOCATIONS,
} from '../../data/seasonalContent';
import '../../styles/explore.scss';
import '../../styles/destinations.scss';
import '../../styles/tourist-nav.scss';

function SeasonalDiscoveryPage() {
	const [destinations, setDestinations] = useState([]);
	const [festivals, setFestivals] = useState([]);
	const [guides, setGuides] = useState([]);
	const [climateData, setClimateData] = useState({});
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		Promise.all([
			getDestinations({ per_page: 50 }),
			loadFestivalsApiFirst(),
			loadGuidesByCategory('seasonal'),
			...CLIMATE_LOCATIONS.map((loc) => showDistrict(loc.slug).then((res) => ({ slug: loc.slug, climate: res.data.climate || [] })).catch(() => ({ slug: loc.slug, climate: [] }))),
		])
			.then(([destRes, festList, guideList, ...climates]) => {
				setDestinations((destRes.data && destRes.data.destinations) || []);
				setFestivals(festList);
				setGuides(guideList);
				const climateMap = {};
				climates.forEach((c) => { climateMap[c.slug] = c.climate; });
				setClimateData(climateMap);
			})
			.finally(() => setLoading(false));
	}, []);

	const autumnDests = destinations.filter((d) => d.best_season && d.best_season.includes('October'));

	if (loading) return <Loading />;

	const bestTimeGuide = guides.find((g) => g.slug === 'best-time-to-visit-nepal');

	return (
		<div className="explore-page tourist-page">
			<Seo
				title="Seasonal Discovery — When to Visit Nepal"
				path="/seasonal-discovery"
				description="The complete guide to Nepal's seasons — month-by-month weather, trekking conditions, festival calendar, wildlife seasons, packing tips, and regional recommendations."
			/>

			<ExploreHero
				image={EXPLORE_IMAGES.seasonal}
				eyebrow="Plan your trip"
				title="When should you visit Nepal?"
				lead="One guide to every season — weather, trails, festivals, wildlife, and what to pack."
				primaryCta={{ to: '#month-guide', label: 'Month-by-month guide' }}
				secondaryCta={{ to: '/#places', label: 'Browse places' }}
				compact
			/>

			<QuickFactsBar facts={[
				{ value: 'Oct–Nov', label: 'Best overall' },
				{ value: 'Mar–May', label: 'Spring alternative' },
				{ value: 'Jun–Sep', label: 'Mustang season' },
				{ value: 'Lunar', label: 'Festival dates' },
			]} />

			<div className="explore-body">
				<DiscoverySection
					eyebrow="The short answer"
					title="Best time to visit — overview"
					intro={bestTimeGuide ? bestTimeGuide.summary : 'October–November and March–May are Nepal\'s two classic windows for mountain views and trekking.'}
				>
					<div className="editorial-columns">
						<div>
							{bestTimeGuide && (
								<p style={{ fontSize: '1rem', lineHeight: 1.75, color: '#3d4554' }}>{bestTimeGuide.body}</p>
							)}
							<div className="editorial-photo">
								<img src={EXPLORE_IMAGES.annapurna} alt="Annapurna range at sunrise" loading="lazy" />
								<p className="editorial-photo__caption">Autumn and spring both deliver Himalayan views — autumn tends toward sharper clarity; spring trades some haze for rhododendron color.</p>
							</div>
						</div>
						<div className="season-grid" style={{ gridTemplateColumns: '1fr' }}>
							{SEASONS.map((s) => (
								<div key={s.id} id={s.id} className="season-card">
									<div className="season-card__image">
										<img src={s.image} alt={s.name} loading="lazy" />
									</div>
									<div className="season-card__body">
										<p className="season-card__months">{s.months}</p>
										<h3 className="season-card__title">{s.name}</h3>
										<p className="season-card__tagline">{s.tagline}</p>
										<p className="season-card__tagline" style={{ marginBottom: '0.5rem' }}>
											<strong>Best for:</strong> {s.bestFor.join(', ')}
										</p>
										<p className="season-card__tagline" style={{ fontSize: '0.85rem', fontStyle: 'italic' }}>{s.avoid}</p>
									</div>
								</div>
							))}
						</div>
					</div>
				</DiscoverySection>

				<DiscoverySection
					id="month-guide"
					eyebrow="Plan month by month"
					title="Month-by-month guide"
					intro="What to expect for trekking, wildlife, and festivals each month — tap a month to see the full picture."
					alt
				>
					<MonthGuide />
				</DiscoverySection>

				<DiscoverySection
					eyebrow="Real numbers"
					title="Weather charts"
					intro="Illustrative climate data from four representative regions — high Himalaya, mid-hills, and Terai lowlands."
				>
					<ClimateChart climateData={climateData} />
				</DiscoverySection>

				<DiscoverySection
					eyebrow="Culture & timing"
					title="Festival calendar"
					intro="Most major festivals follow lunar calendars — dates shift every year. Plan around the season, then confirm exact dates closer to travel."
					linkTo="/festivals"
					linkLabel="All festivals"
					alt
				>
					<div className="info-card-grid">
						{festivals.map((f) => (
							<Link key={f.slug} to={`/festivals/${f.slug}`} className="info-card" style={{ textDecoration: 'none', color: 'inherit' }}>
								<div className="info-card__body">
									<p className="info-card__label">{f.typical_month || 'Annual'}</p>
									<h3 className="info-card__title">{f.name}</h3>
									<p className="info-card__text">{f.tagline || f.summary}</p>
								</div>
							</Link>
						))}
						<div className="info-card">
							<div className="info-card__body">
								<p className="info-card__label">Important</p>
								<h3 className="info-card__title">Lunar calendar shifts</h3>
								<p className="info-card__text">Dashain, Tihar, Holi, and Buddha Jayanti land on different Gregorian dates each year. Check the <Link to="/festivals">Festivals Hub</Link> before booking flights.</p>
							</div>
						</div>
					</div>
				</DiscoverySection>

				<DiscoverySection
					eyebrow="On the trail"
					title="Trekking conditions by season"
					intro="The same month means something completely different at 1,300m in Kathmandu versus 5,300m at Everest Base Camp."
				>
					<div className="info-card-grid">
						<div className="info-card">
							<div className="info-card__image">
								<img src={EXPLORE_IMAGES.trekking} alt="Trekkers on a Himalayan trail" loading="lazy" />
							</div>
							<div className="info-card__body">
								<p className="info-card__label">Peak trekking</p>
								<h3 className="info-card__title">October – November</h3>
								<p className="info-card__text">Clearest skies, stable weather, crowded trails. Book Lukla flights and popular teahouses weeks ahead. EBC and ABC are at their best.</p>
							</div>
						</div>
						<div className="info-card">
							<div className="info-card__image">
								<img src="https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=600&q=80" alt="Spring rhododendron blooms" loading="lazy" />
							</div>
							<div className="info-card__body">
								<p className="info-card__label">Spring trekking</p>
								<h3 className="info-card__title">March – May</h3>
								<p className="info-card__text">Warmer, rhododendron forests in bloom across the mid-hills. Slightly hazier than autumn but excellent for Annapurna and Langtang routes.</p>
							</div>
						</div>
						<div className="info-card">
							<div className="info-card__image">
								<img src={EXPLORE_IMAGES.himalaya} alt="Snow-covered high pass" loading="lazy" />
							</div>
							<div className="info-card__body">
								<p className="info-card__label">Rain shadow</p>
								<h3 className="info-card__title">June – September in Mustang</h3>
								<p className="info-card__text">While the rest of Nepal is wet, Upper Mustang and Manang stay relatively dry. The classic routes are miserable — the rain shadow is the exception.</p>
							</div>
						</div>
					</div>
					{autumnDests.length > 0 && (
						<div className="mt-4">
							<h3 style={{ fontSize: '1.1rem', color: '#253f72', marginBottom: '1rem' }}>Top autumn destinations</h3>
							<div className="destination-rail">
								{autumnDests.slice(0, 5).map((d) => (
									<DestinationCard key={d.slug} destination={d} />
								))}
							</div>
						</div>
					)}
				</DiscoverySection>

				<DiscoverySection
					eyebrow="Jungle & lowlands"
					title="Wildlife seasons"
					intro="Chitwan and the Terai follow a different rhythm from the mountains — heat and humidity matter as much as rain."
					alt
				>
					<div className="editorial-columns">
						<p style={{ fontSize: '1rem', lineHeight: 1.75, color: '#3d4554' }}>
							<strong>October – March</strong> is prime wildlife season in Chitwan: comfortable temperatures, good rhino and bird sightings, and reliable safari conditions. Early-morning canoe trips consistently outperform afternoon jeep safaris.
						</p>
						<div className="editorial-photo">
							<img src={EXPLORE_IMAGES.wildlife} alt="One-horned rhinoceros in Chitwan" loading="lazy" />
							<p className="editorial-photo__caption">Chitwan National Park — best visited in the cooler, drier months.</p>
						</div>
					</div>
					<div className="destination-rail mt-3">
						{destinations.filter((d) => d.category === 'national_park' && d.district && d.district.name === 'Chitwan').map((d) => (
							<DestinationCard key={d.slug} destination={d} />
						))}
					</div>
				</DiscoverySection>

				<DiscoverySection
					eyebrow="Where & when"
					title="Regional recommendations"
					intro="Each region of Nepal has its own seasonal sweet spot — match your destination to the right window."
				>
					<div className="info-card-grid">
						{REGIONAL_RECS.map((rec) => (
							<div key={rec.region} className="info-card">
								<div className="info-card__image">
									<img src={rec.image} alt={rec.region} loading="lazy" />
								</div>
								<div className="info-card__body">
									<p className="info-card__label">Best: {rec.best}</p>
									<h3 className="info-card__title">{rec.region}</h3>
									<p className="info-card__text">{rec.summary}</p>
									<div className="related-rail mt-2">
										{rec.destinations.map((slug) => (
											<Link key={slug} to={`/destinations/${slug}`} className="related-link">{slug.replace(/-/g, ' ')}</Link>
										))}
									</div>
								</div>
							</div>
						))}
					</div>
				</DiscoverySection>

				<DiscoverySection
					eyebrow="What to bring"
					title="Packing guide by season"
					intro="Layers matter more than any single item — Nepal's temperature swings by altitude and season more than most countries."
					alt
				>
					<div className="info-card-grid">
						{PACKING_GUIDE.map((pack) => (
							<div key={pack.season} className="info-card">
								<div className="info-card__body">
									<p className="info-card__label">Packing</p>
									<h3 className="info-card__title">{pack.season}</h3>
									<div className="info-card__text">
										<ul>
											{pack.items.map((item) => <li key={item}>{item}</li>)}
										</ul>
									</div>
								</div>
							</div>
						))}
					</div>
				</DiscoverySection>

				<DiscoverySection
					eyebrow="Capture it"
					title="Photography tips"
					intro="Light, timing, and cultural respect — what photographers learn after their first Nepal trip."
				>
					<div className="info-card-grid">
						{PHOTO_TIPS.map((tip) => (
							<div key={tip.title} className="info-card">
								<div className="info-card__body">
									<h3 className="info-card__title">{tip.title}</h3>
									<p className="info-card__text">{tip.body}</p>
								</div>
							</div>
						))}
					</div>
				</DiscoverySection>

				<DiscoverySection
					eyebrow="Plan your spend"
					title="Budget considerations"
					intro="Season affects prices as much as destination — peak autumn costs more across the board."
					alt
				>
					<div className="info-card-grid">
						{BUDGET_SEASONS.map((b) => (
							<div key={b.level} className="info-card">
								<div className="info-card__body">
									<h3 className="info-card__title">{b.level}</h3>
									<p className="info-card__text">{b.impact}</p>
									<p className="info-card__text" style={{ marginTop: '0.5rem', fontStyle: 'italic' }}><strong>Tip:</strong> {b.tip}</p>
								</div>
							</div>
						))}
					</div>
				</DiscoverySection>

				<DiscoverySection
					eyebrow="Common questions"
					title="Frequently asked questions"
				>
					<FaqAccordion items={SEASONAL_FAQS} />
				</DiscoverySection>

				<DiscoverySection
					eyebrow="Go deeper"
					title="Related destinations"
					intro="Places where season matters most — each page includes best/worst season and monthly climate."
					alt
				>
					<div className="destination-rail">
						{destinations.slice(0, 8).map((d) => (
							<DestinationCard key={d.slug} destination={d} />
						))}
					</div>
				</DiscoverySection>

				<DiscoverySection
					eyebrow="Trip ideas"
					title="Suggested itineraries"
					intro="Curated routes that match Nepal's seasonal windows — more itineraries coming as we expand the database."
				>
					<div className="info-card-grid">
						{SUGGESTED_ITINERARIES.map((it) => (
							<div key={it.name} className="itinerary-card">
								<p className="itinerary-card__days">{it.days}</p>
								<h3 className="itinerary-card__title">{it.name}</h3>
								<p className="itinerary-card__summary">{it.summary}</p>
								<p className="itinerary-card__stops">
									{it.destinations.map((slug, i) => (
										<span key={slug}>
											{i > 0 && ' → '}
											<Link to={`/destinations/${slug}`}>{slug.replace(/-/g, ' ')}</Link>
										</span>
									))}
								</p>
							</div>
						))}
					</div>
				</DiscoverySection>

				<DiscoverySection
					eyebrow="Practical depth"
					title="Related guides"
					intro="Deeper reading on permits, transport, and on-the-ground logistics."
					alt
				>
					<div className="related-rail">
						{guides.map((g) => (
							<Link key={g.slug} to={`/guides/${g.slug}`} className="related-link">{g.title}</Link>
						))}
						<Link to="/government" className="related-link">Visa & permits</Link>
						<Link to="/transportation" className="related-link">Getting around</Link>
						<Link to="/festivals" className="related-link">Festival calendar</Link>
					</div>
				</DiscoverySection>

				<section className="discovery-section">
					<div className="container">
						<div className="explore-cta-band">
							<h3>Know your season — now pick your places</h3>
							<p>Every destination page includes best season, difficulty, cost, and monthly climate for its district.</p>
							<Link to="/destinations" className="explore-hero__cta explore-hero__cta--primary">Explore all destinations</Link>
						</div>
					</div>
				</section>
			</div>
		</div>
	);
}

export default SeasonalDiscoveryPage;
